/**
 * 71.2b (DECISIONS 324 (3), ARCHITECTURE §5.36 (2)): the fine relief's shares by the ground's material (RELIEF_BY_MATERIAL)
 * at a point of the coarse mesh — continuous across the facets' edges. A facet's material is its own (63.5's
 * facetMaterial, dry), so the shares are kept per welded vertex (the mean of the facets round it) and read at a point by
 * its facet's barycentric weights: two facets that share an edge agree along it, and the relief has no step there.
 *
 * Kept per heightmap identity (a crater makes a new heightmap; a facet dug changes its material, the stratum), a vertex's
 * shares made when first asked; the weld per basePositions (they never change). Bit for bit today's hummocks where a
 * facet and every facet round its corners are soil, ash or mud (the shares taken as they are, not averaged).
 */
import type { PlanetState, Vec3 } from '../types';
import { RELIEF_BY_MATERIAL, RELIEF_SNOW_THIN } from '../constants/ground';
import { facetMaterialCached, facetLayerDepth } from './groundMaterial';
import { nearestSurfaceVertex } from './surface';
import { simWork } from './workCounters';

/** The mesh's vertex entries (each triangle its own three) welded by position: each entry's vertex, and each vertex's facets (packed). */
export interface Weld { vertexOf: Int32Array; facetsAt: Int32Array; facets: Int32Array }
const welds = new WeakMap<Float32Array, Weld>();

/** The weld for `bp` (built once per basePositions; 71.4's hand-on sums its lift on the same vertices). */
export function meshWeld(bp: Float32Array): Weld { return weldOf(bp); }
function weldOf(bp: Float32Array): Weld {
  let w = welds.get(bp);
  if (w) return w;
  const entries = Math.floor(bp.length / 3);
  const byPos = new Int32Array(entries);
  for (let i = 0; i < entries; i++) byPos[i] = i;
  byPos.sort((i, j) => (bp[i * 3] - bp[j * 3]) || (bp[i * 3 + 1] - bp[j * 3 + 1]) || (bp[i * 3 + 2] - bp[j * 3 + 2]) || i - j);
  const vertexOf = new Int32Array(entries);
  let V = 0;
  for (let k = 0; k < entries; k++) {
    const i = byPos[k], j = k > 0 ? byPos[k - 1] : -1;
    if (j >= 0 && bp[i * 3] === bp[j * 3] && bp[i * 3 + 1] === bp[j * 3 + 1] && bp[i * 3 + 2] === bp[j * 3 + 2]) vertexOf[i] = vertexOf[j];
    else vertexOf[i] = V++;
  }
  // each vertex's facets, ascending (an entry i is facet ⌊i/3⌋'s corner)
  const count = new Int32Array(V + 1);
  for (let i = 0; i < entries; i++) count[vertexOf[i] + 1]++;
  for (let v = 0; v < V; v++) count[v + 1] += count[v];
  const facets = new Int32Array(entries), fill = count.slice(0, V);
  for (let i = 0; i < entries; i++) facets[fill[vertexOf[i]]++] = Math.floor(i / 3);
  w = { vertexOf, facetsAt: count, facets };
  welds.set(bp, w);
  return w;
}

/** Per heightmap identity: each welded vertex's shares (broad, fine, ripple) and whether they are made. */
interface Shares { at: Float64Array; made: Uint8Array }
const kept = new WeakMap<Float32Array, Shares>();

const facetShare = new Float64Array(3);
/** Facet `t`'s own shares into `facetShare`. */
function facetShares(planet: PlanetState, t: number): void {
  const m = facetMaterialCached(planet, t), row = RELIEF_BY_MATERIAL[m];
  facetShare[0] = m === 'snow' ? RELIEF_SNOW_THIN + (row.broad - RELIEF_SNOW_THIN) * facetLayerDepth(planet, t) : row.broad;
  facetShare[1] = row.fine; facetShare[2] = row.ripple;
}

/** Vertex `v`'s shares (made once per heightmap): its facets' mean — or, where they all agree, theirs as they are. */
function vertexShares(planet: PlanetState, w: Weld, s: Shares, v: number): void {
  if (s.made[v]) return;
  simWork.reliefVertices++;
  const from = w.facetsAt[v], to = w.facetsAt[v + 1];
  let b = 0, f = 0, r = 0, same = true, b0 = 0, f0 = 0, r0 = 0;
  for (let q = from; q < to; q++) {
    facetShares(planet, w.facets[q]);
    if (q === from) { b0 = facetShare[0]; f0 = facetShare[1]; r0 = facetShare[2]; } else if (facetShare[0] !== b0 || facetShare[1] !== f0 || facetShare[2] !== r0) same = false;
    b += facetShare[0]; f += facetShare[1]; r += facetShare[2];
  }
  const n = to - from, o = v * 3;
  if (same || n === 0) { s.at[o] = b0; s.at[o + 1] = f0; s.at[o + 2] = r0; } else { s.at[o] = b / n; s.at[o + 1] = f / n; s.at[o + 2] = r / n; }
  s.made[v] = 1;
}

function sharesOf(planet: PlanetState, w: Weld): Shares {
  let s = kept.get(planet.heightmap);
  if (!s) {
    const V = w.facetsAt.length - 1;
    s = { at: new Float64Array(V * 3), made: new Uint8Array(V) };
    kept.set(planet.heightmap, s);
  }
  return s;
}

/**
 * The relief's shares at a point of facet `t` with barycentric (u, v) (lastSurfaceTriangle / lastSurfaceU / V), into
 * `out` [broad, fine, ripple]. `t` < 0 (the lookup fell back to a vertex): the nearest vertex's to `dir`.
 */
export function reliefShares(planet: PlanetState, t: number, u: number, v: number, dir: Vec3, out: Float64Array): void {
  inBuf[0] = dir.x; inBuf[1] = dir.y; inBuf[2] = dir.z; inBuf[3] = t; inBuf[4] = u; inBuf[5] = v;
  reliefSharesIn(planet, inBuf, 0, 3, out);
}
const inBuf = new Float64Array(6);

/**
 * reliefShares with the direction read from `buf` [dirAt .. dirAt + 2] and the facet and its (u, v) from [at .. at + 2] —
 * the hot caller's way in (fineTerrain's fineIn: the direction [0..2], the facet [7..9]; DECISIONS 384, 62.64's rule — no
 * doubles across a call TurboFan does not inline, nor stored into an object's fields per lookup).
 */
export function reliefSharesIn(planet: PlanetState, buf: Float64Array, dirAt: number, at: number, out: Float64Array): void {
  const t = buf[at];
  const w = weldOf(planet.basePositions), s = sharesOf(planet, w);
  if (t < 0) {
    const vx = w.vertexOf[nearestSurfaceVertex(planet, { x: buf[dirAt], y: buf[dirAt + 1], z: buf[dirAt + 2] })];
    vertexShares(planet, w, s, vx);
    out[0] = s.at[vx * 3]; out[1] = s.at[vx * 3 + 1]; out[2] = s.at[vx * 3 + 2];
    return;
  }
  const a = w.vertexOf[t * 3], b = w.vertexOf[t * 3 + 1], c = w.vertexOf[t * 3 + 2];
  vertexShares(planet, w, s, a); vertexShares(planet, w, s, b); vertexShares(planet, w, s, c);
  const A = s.at, ao = a * 3, bo = b * 3, co = c * 3;
  if (A[ao] === A[bo] && A[ao] === A[co] && A[ao + 1] === A[bo + 1] && A[ao + 1] === A[co + 1] && A[ao + 2] === A[bo + 2] && A[ao + 2] === A[co + 2]) {
    out[0] = A[ao]; out[1] = A[ao + 1]; out[2] = A[ao + 2];
    return;
  }
  // the weights, held inside the facet (an edge's hit may sit a hair outside it)
  const wb = Math.min(1, Math.max(0, buf[at + 1])), wc = Math.min(1, Math.max(0, buf[at + 2])), wa = Math.max(0, 1 - wb - wc);
  for (let k = 0; k < 3; k++) out[k] = Math.max(0, wa * A[ao + k] + wb * A[bo + k] + wc * A[co + k]);
}

/**
 * The faces whose relief a change to `faces` can move (71.2b): a face's material reads its own heights, and its shares
 * reach every face round its three corners — so a crater's faces, and each face that touches one of their corners.
 */
export function reliefFacesAround(planet: PlanetState, faces: Iterable<number>): Set<number> {
  const w = weldOf(planet.basePositions), out = new Set<number>();
  for (const t of faces) {
    for (let c = 0; c < 3; c++) {
      const v = w.vertexOf[t * 3 + c];
      for (let q = w.facetsAt[v]; q < w.facetsAt[v + 1]; q++) out.add(w.facets[q]);
    }
  }
  return out;
}

/** 71.2b: how many of a face's longest edges from a moved vertex the relief can move — two by its shares (one by the mesh alone). */
export function reliefEdgesMoved(materialRelief: boolean): number { return materialRelief ? 2 : 1; }

/** 71.2b: whether face `t` and every face round its corners have the same heights in `a` and `b` — its relief by material the same. */
export function sameReliefAround(planet: PlanetState, a: Float32Array, b: Float32Array, t: number): boolean {
  if (t < 0 || a.length !== b.length) return false;
  const w = weldOf(planet.basePositions);
  for (let c = 0; c < 3; c++) {
    const v = w.vertexOf[t * 3 + c];
    for (let q = w.facetsAt[v]; q < w.facetsAt[v + 1]; q++) {
      const i = w.facets[q] * 3;
      if (a[i] !== b[i] || a[i + 1] !== b[i + 1] || a[i + 2] !== b[i + 2]) return false;
    }
  }
  return true;
}
